import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { CameraControls, useProgress, useGLTF, useTexture } from '@react-three/drei';
import { AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { mainSelector, SetAnimating, SetAnimation, SetCurrentDistance, SetSound, SwitchSection } from '../store/slices/main.slice';
import { AnimationAction, AnimationMixer, Box3, LoopOnce, Quaternion, Vector3 } from 'three';
import ARModalRoot from './modals/ARModalRoot';
import { isMobile, isTablet } from 'react-device-detect';

const HomeContent = React.lazy(() => import('./HomeContent'));
const PalazzoContent = React.lazy(() => import('./PalazzoContent'));
const ShapesModalRoot = React.lazy(() => import('./modals/ShapesModalRoot'));
const MenuModalRoot = React.lazy(() => import('./modals/MenuModalRoot'));

const HomePage = () => {
    const dispatch: any = useDispatch();
    const { active, progress, loaded, total } = useProgress();
    const [loading, setLoading] = useState<boolean>(true);
    const { section, zoom, sound, animation, currentDistance, lastAnimation, isAnimating } = useSelector(mainSelector);
    const [maxPolarAngle, setMaxPolarAngle] = useState(Math.PI);
    const cameraControlRef: any = useRef<CameraControls | null>(null);
    const [modelReady, setModelReady] = useState(false);
    const [bgReady, setBgReady] = useState(false);
    const [frameMode, setFrameMode] = useState<'always' | 'demand'>('always');
    const initialApplied = useRef(false);
    const modelGroupRef = useRef<any>(null);
    const sceneCenterRef = useRef(new Vector3(0, 0, 0)); // Group position = rotation center
    const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

    // Detect orientation change
    useEffect(() => {
        const handleOrientationChange = () => {
            setIsLandscape(window.innerWidth > window.innerHeight);
        };
        window.addEventListener('resize', handleOrientationChange);
        return () => window.removeEventListener('resize', handleOrientationChange);
    }, []);

    useGLTF.preload('images/PalazzoDucale7_Anim.glb');
    useGLTF.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');

    const Scene = React.memo(({ animation, onReady, groupRef, isLandscape, section }: { animation: string | number | null; onReady?: () => void; groupRef: React.MutableRefObject<any>; isLandscape: boolean; section: number }) => {
        const modelRef = useRef<any>();
        const mixer = useRef<AnimationMixer | null>(null);

        const actionsByIndex = useRef<AnimationAction[]>([]);
        const nameToIndex = useRef<Record<string, number>>({});
        const currentAction = useRef<AnimationAction | null>(null);

        const initialPosition = useRef(new Vector3());
        const initialRotation = useRef(new Quaternion());
        const initialScale = useRef(new Vector3());

        const currentIndexRef = useRef<number | null>(null);
        const isLocked = useRef(false);
        const isReversing = useRef(false);
        const listenerAttached = useRef(false);

        // guard anti-retrigger della stessa animazione (anche dopo remount/ri-render)
        const lastPlayedKeyRef = useRef<string | null>(null); // <<<

        const gltf = useGLTF('images/PalazzoDucale7_Anim.glb', true, true);

        useEffect(() => {
            if (!gltf?.scene) return;
            // Calculate the bounding box center - this is where camera should look for rotation
            const box = new Box3().setFromObject(gltf.scene);
            const center = new Vector3();
            box.getCenter(center);
            // Use this center as the rotation point for camera
            sceneCenterRef.current.copy(center);
            onReady?.();
        }, [gltf, onReady, groupRef]);

        useEffect(() => {
            if (!gltf?.scene || !gltf.animations?.length) return;
            if (mixer.current) return;

            mixer.current = new AnimationMixer(gltf.scene);

            actionsByIndex.current = gltf.animations.map((clip, i) => {
                const a = mixer.current!.clipAction(clip);
                a.setLoop(LoopOnce, 1);
                a.clampWhenFinished = true;
                a.enabled = true;
                if (nameToIndex.current[clip.name] === undefined) nameToIndex.current[clip.name] = i;
                return a;
            });

            initialPosition.current.copy(gltf.scene.position);
            initialRotation.current.copy(gltf.scene.quaternion);
            initialScale.current.copy(gltf.scene.scale);

            const onFinished = () => {
                isLocked.current = false;
                currentAction.current = null;

                if (isReversing.current) {
                    // Fine del RESET: sblocca e torna idle pulito
                    isReversing.current = false;
                    dispatch(SetAnimating(false));
                    dispatch(SetAnimation(null));
                    lastPlayedKeyRef.current = null; // <<< permette nuove clip identiche in futuro
                } else {
                    // Fine FORWARD: NIENTE SetAnimation(null) per evitare “scatto”
                    // Manteniamo il lock globale attivo finché non fai il reset (bottoni disabilitati)
                    // Se vuoi abilitare i bottoni anche qui, decommenta la riga sotto:
                    // dispatch(SetAnimating(false));
                }
            };

            if (!listenerAttached.current) {
                mixer.current.addEventListener('finished', onFinished);
                listenerAttached.current = true;
            }

            return () => {
                if (listenerAttached.current) {
                    mixer.current?.removeEventListener('finished', onFinished);
                    listenerAttached.current = false;
                }
                actionsByIndex.current.forEach((a) => a.stop());
                mixer.current?.stopAllAction();
            };
        }, [gltf, dispatch]);

        const getIndexForKey = (key: string | number): number => {
            if (!gltf?.animations?.length) return -1;
            if (typeof key === 'number') return key >= 0 && key < gltf.animations.length ? key : -1;
            const maybeNum = Number(key);
            if (!Number.isNaN(maybeNum)) return maybeNum >= 0 && maybeNum < gltf.animations.length ? maybeNum : -1;
            if (nameToIndex.current[key] !== undefined) return nameToIndex.current[key];
            const idx = gltf.animations.findIndex((c) => c.name === key);
            return idx;
        };

        const resolveActionByIndex = (idx: number): AnimationAction | null =>
            idx >= 0 && idx < actionsByIndex.current.length ? actionsByIndex.current[idx] : null;

        const playActionByIndex = (idx: number) => {
            const next = resolveActionByIndex(idx);
            if (!next) return;

            // evita doppio avvio stessa clip
            // @ts-ignore
            if (currentAction.current === next && next.isRunning?.()) return;
            if (isLocked.current && currentAction.current && currentAction.current !== next) return;

            const prev = currentAction.current;
            if (prev && prev !== next) {
                prev.fadeOut(0.2);
                prev.stop();
            }

            next.enabled = true;
            next.reset();
            next.setEffectiveTimeScale(1);
            next.setEffectiveWeight(1);
            next.fadeIn(0.2);
            next.play();

            currentAction.current = next;
            currentIndexRef.current = idx;
            isLocked.current = true;
            dispatch(SetAnimating(true));
        };

        useEffect(() => {
            if (!mixer.current || !gltf?.animations?.length) return;

            // START FORWARD
            if (animation !== null && animation !== 'reset') {
                const idx = getIndexForKey(animation);
                if (idx < 0) return;

                // guard anti-retrigger: se abbiamo già suonato "questa" chiave, non ripartire
                const key = typeof animation === 'number' ? `idx:${idx}` : `name:${animation}`; // <<<
                if (lastPlayedKeyRef.current === key) return; // <<<

                // Ripristino posa e camera per l'animazione forward (una volta sola)
                gltf.scene.position.copy(initialPosition.current);
                gltf.scene.quaternion.copy(initialRotation.current);
                gltf.scene.scale.copy(initialScale.current);

                // Use sceneCenterRef as target for animations
                cameraControlRef.current?.setLookAt(73, 50, -14, sceneCenterRef.current.x, sceneCenterRef.current.y, sceneCenterRef.current.z, true);
                cameraControlRef.current?.zoomTo(1.5, true);

                if (!isLocked.current) {
                    playActionByIndex(idx);
                    lastPlayedKeyRef.current = key; // <<< segna che l’abbiamo avviata
                }
            }

            // START RESET (reverse della lastAnimation)
            if (animation === 'reset') {
                if(lastAnimation === null && animation === 'reset') return
                const idx = getIndexForKey(lastAnimation as any);
                const act = resolveActionByIndex(idx);
                if (!act) return;

                actionsByIndex.current.forEach((a, i) => {
                    if (i !== idx) {
                        a.stop();
                        a.enabled = false;
                    }
                });

                cameraControlRef.current?.setLookAt(56, 18.48, -19, sceneCenterRef.current.x, sceneCenterRef.current.y, sceneCenterRef.current.z, true);
                cameraControlRef.current?.zoomTo(1, true);
                cameraControlRef.current?.dollyTo(100, true);

                currentAction.current?.stop();

                const dur = act.getClip().duration;
                act.enabled = true;
                act.clampWhenFinished = true;
                act.setLoop(LoopOnce, 1);
                act.reset();
                act.paused = false;
                act.time = Math.max(0, dur - 1e-3);
                act.setEffectiveTimeScale(-1);
                act.setEffectiveWeight(1);
                act.play();

                currentAction.current = act;
                currentIndexRef.current = idx;
                isLocked.current = true;
                isReversing.current = true;
                dispatch(SetAnimating(true));
            }
        }, [animation, gltf, dispatch]);

        useFrame((_, delta) => {
            if (mixer.current) mixer.current.update(delta);
        });

        useFrame(() => {
            if (cameraControlRef.current && animation === null) {
                dispatch(SetCurrentDistance(cameraControlRef.current.distance));
            }
        });

        // Calculate scale: 1.15x only in mobile landscape when in palazzo section
        const modelScale = (isMobile || isTablet) && isLandscape && section === 1 ? 1.15 : 1;

        return (
            <group ref={groupRef}>
                <primitive ref={modelRef} object={gltf.scene} scale={modelScale} castShadow receiveShadow />
            </group>
        );
    });

    function Skybox({ onReady }: { onReady?: () => void }) {
        const texture: any = useTexture('images/panorama_drone_v3.jpg');
        useEffect(() => {
            if (texture) onReady?.();
        }, [texture, onReady]);
        return (
            <mesh position={[0, 0, 0]}>
                <sphereGeometry args={[500, 60, 40]} />
                <meshBasicMaterial map={texture} side={2} />
            </mesh>
        );
    }

    const DistanceUpdater = ({ cameraRef, currentSection, animating }: any) => {
        const lastDistanceRef = useRef<number>(0);

        useFrame(() => {
            if (cameraRef.current && currentSection === 1 && !animating) {
                const distance = cameraRef.current.distance;
                // Update only if distance has actually changed
                if (Math.abs(distance - lastDistanceRef.current) > 0.1) {
                    lastDistanceRef.current = distance;
                    dispatch(SetCurrentDistance(distance));
                }
            }
        });
        return null;
    };


    const initialPositioning = (immediate = false) => {
        if (!cameraControlRef.current) return;
        const controls = cameraControlRef.current as CameraControls;
        const prevSmooth = (controls as any).smoothTime;
        if (immediate) (controls as any).smoothTime = 0;
        controls.setLookAt(32.93, 19.12, 11.33, sceneCenterRef.current.x, sceneCenterRef.current.y, sceneCenterRef.current.z, !immediate);
        controls.zoomTo(1.5, !immediate);
        if (immediate) (controls as any).smoothTime = prevSmooth ?? 1;
    };

    useEffect(() => {
        const allLoaded = !active && progress === 100 && loaded === total && modelReady && bgReady;
        if (allLoaded && !initialApplied.current) {
            initialApplied.current = true;
            initialPositioning(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setLoading(false);
                    setFrameMode('demand');
                });
            });
        }
    }, [active, progress, loaded, total, modelReady, bgReady]);

    useEffect(() => {
        if (!loading && section === 0) {
            initialPositioning(false);
            setFrameMode('demand');
        }
        if (!loading && section === 1) {
            changePosition();
            setFrameMode('always'); // Always update in palazzo section for slider
        }
    }, [loading, section]);

    useEffect(() => {
        if (cameraControlRef.current) {
            const zoomLevel = 1 + zoom / 100;
            cameraControlRef.current.zoomTo(zoomLevel, true);
        }
    }, [zoom]);

    const changePosition = () => {
        setMaxPolarAngle(Math.PI / 2 + 1e-3);
        dispatch(SwitchSection(1));
        if (cameraControlRef.current) (cameraControlRef.current as any).smoothTime = 1;
        // Use sceneCenterRef as target - the group position (0,0,0)
        // Adjusted camera position for clockwise rotation to make model more frontal (+ additional 10°)
        cameraControlRef.current?.setLookAt(56, 18.48, -19, sceneCenterRef.current.x, sceneCenterRef.current.y, sceneCenterRef.current.z, true);
        cameraControlRef.current?.zoomTo(1, true);
        cameraControlRef.current?.dollyTo(100, true);
        // Update currentDistance immediately so slider shows correct position
        dispatch(SetCurrentDistance(100));
    };

    const handleZoom = (constant: number) => {
        cameraControlRef.current?.dollyTo((constant / 100) * 190 + 10, true);
    };

    const handleSound = () => {
        dispatch(SetSound(!sound));
    };

    const CustomLoader = () => {
        return (
            <div className="w-[100vw] h-[100vh] flex flex-col justify-between items-center bg-[#1a1a1a] py-0 sm:py-[50px] pt-safe sm:pt-safe pb-safe sm:pb-0">
                <img src={'/images/GNDM acronimo orizzontale white.png'} alt="Header GNDM Logo" className="max-h-[20px]"/>
                <div className="flex flex-col justify-center items-center gap-[20px] sm:gap-[30px] lg:gap-[50px]">
                    <img src={'/images/loading.png'} alt="Loader" className="max-h-[60px] lg:max-h-[100px]"/>
                    <span className="text-[#717171] text-[20px] font-untitledSans">Loading 3D</span>
                    <span className="text-[#717171] text-[20px] font-untitledSans mt-[-10px] sm:mt-[-20px] md:mt-[-30px]">
            {progress.toFixed()}%
          </span>
                </div>
                <div className="pb-[50px]">
                    <button onClick={handleSound} className="w-[25px] h-[25px] min-w-[25px] min-h-[25px] flex flex-col items-center justify-center cursor-pointer">
                        <img src={sound ? '/images/sound-icon.png' : '/images/sound_off.png'} alt="Sound icon" className="h-full"/>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="h-[100vh] w-[100vw] overflow-hidden">
            {!loading && (
                <AnimatePresence>
                    <Suspense fallback={null}>
                        {section === 0 && <HomeContent key="home" changePosition={changePosition} />}
                        {section === 1 && <PalazzoContent key="palazzo" handleZoom={handleZoom} currentDistance={currentDistance} />}
                        <ShapesModalRoot />
                        <ARModalRoot />
                    </Suspense>
                </AnimatePresence>
            )}
            <div className="absolute top-0 left-0 h-full w-full">
                {loading && <CustomLoader />}
                <Canvas camera={{ position: [0, 0, 0] }} frameloop={frameMode}>
                    <Suspense fallback={null}>
                        <CameraControls
                            ref={cameraControlRef}
                            makeDefault
                            maxPolarAngle={maxPolarAngle}
                            maxDistance={200}
                            minDistance={10}
                        />
                        <Scene animation={animation} onReady={() => setModelReady(true)} groupRef={modelGroupRef} isLandscape={isLandscape} section={section} />
                        <DistanceUpdater cameraRef={cameraControlRef} currentSection={section} animating={isAnimating} />
                        <ambientLight intensity={3} />
                        <Skybox onReady={() => setBgReady(true)} />
                    </Suspense>
                </Canvas>
            </div>
        </div>
    );
};

export default HomePage;